import AddShoppingCartOutlinedIcon from '@mui/icons-material/AddShoppingCartOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import FootprintIcon from '@mui/icons-material/SelectAll';
import Grow from '@mui/material/Grow';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import PropTypes from 'prop-types';
import React, {useEffect, useRef, useState} from 'react';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {KEY_ESCAPE} from 'keycode-js';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';

import useOnKeyDown from '@explorer/hooks/useOnKeyDown';
import {removeAllFromOrder, removeAllFromView} from '@explorer/actions/results';
import {trackEvent} from '@explorer/analytics';
import {unfocusAllItems} from '@explorer/actions/focused-item';

const DeselectMenuButton = ({...props}) => {
  const {t} = useTranslation();
  const options = [
    {
      icon: undefined,
      text: t('deselectMenu.all'),
      callback: () => {
        props.removeAllFromView();
        props.removeAllFromOrder();
        props.unfocusAllItems();
        trackEvent('Deselect Menu: Deselect All Clicked', {
          orderItemsCount: props.orderedItems.length,
          footprintsCount: props.focusedItems.length,
          viewItemsCount: props.viewedItems.length,
        });
      },
    },
    {
      icon: <AddShoppingCartOutlinedIcon />,
      text: t('deselectMenu.cart'),
      callback: () => {
        props.removeAllFromOrder();
        trackEvent('Deselect Menu: Deselect Scenes In Cart Clicked', {
          orderItemsCount: props.orderedItems.length,
        });
      },
    },
    {
      icon: <FootprintIcon />,
      text: t('deselectMenu.footprints'),
      callback: () => {
        props.unfocusAllItems();
        trackEvent('Deselect Menu: Deselect Item Footprints Clicked', {
          footprintsCount: props.focusedItems.length,
        });
      },
    },
    {
      icon: <VisibilityOffIcon />,
      text: t('deselectMenu.previews'),
      callback: () => {
        props.removeAllFromView();
        trackEvent('Deselect Menu: Deselect Scene Previews Clicked', {
          viewItemsCount: props.viewedItems.length,
        });
      },
    },
  ];

  const [open, setOpen] = useState(false);
  const anchorRef = useRef();
  const [buttonEnabled, setButtonEnabled] = useState(false);

  const handleSelectedButtonClick = callback => callback();

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (
      props.orderedItems.length === 0 &&
      props.viewedItems.length === 0 &&
      props.focusedItems.length === 0
    ) {
      handleClose();
      setButtonEnabled(false);
    } else {
      setButtonEnabled(true);
    }
  }, [props.orderedItems, props.viewedItems, props.focusedItems]);

  useOnKeyDown(KEY_ESCAPE, handleClose);

  return (
    <React.Fragment>
      <ButtonGroup
        variant="outlined"
        ref={anchorRef}
        aria-label="split button"
        size="small"
      >
        <Button
          onClick={() => handleSelectedButtonClick(options[0].callback)}
          disabled={!buttonEnabled}
          sx={{padding: theme => theme.spacing(0.5, 0.75)}}
        >
          {options[0].text}
        </Button>
        <Button
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="menu"
          aria-label="deselect-menu-button"
          disabled={!buttonEnabled}
          onClick={handleToggle}
          size="small"
          variant="outlined"
          sx={{padding: theme => theme.spacing(0.5, 0.75)}}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{
          zIndex: 1,
        }}
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        role={undefined}
      >
        {({TransitionProps, placement}) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  <ListItem divider sx={{fontWeight: 'bold'}}>
                    {t('deselectMenu.deselect')}
                  </ListItem>
                  {options.slice(1).map(option => (
                    <MenuItem
                      key={option.text}
                      onClick={() => handleSelectedButtonClick(option.callback)}
                    >
                      <ListItemIcon>{option.icon}</ListItemIcon>
                      <ListItemText>{option.text}</ListItemText>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
};

DeselectMenuButton.propTypes = {
  orderedItems: PropTypes.array,
  viewedItems: PropTypes.array,
  focusedItems: PropTypes.array,
  removeAllFromView: PropTypes.func,
  removeAllFromOrder: PropTypes.func,
  unfocusAllItems: PropTypes.func,
};

const mapStateToProps = state => ({
  orderedItems: state.results.orderItems,
  viewedItems: state.results.viewItems,
  focusedItems: state.focusedItems,
});

const mapDispatchProps = {
  removeAllFromView,
  removeAllFromOrder,
  unfocusAllItems,
};

export default connect(mapStateToProps, mapDispatchProps)(DeselectMenuButton);
